import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { OfferTypes } from '../../types/enums';
import { ProductViewModel } from '../../types/generated';
import { getProductQuantityInBasket } from '../product/productPageApi';
import { OfferDetails } from './Offer';

export default function OfferPrompt({
  offer,
  handleQuantityChange,
  basketQty,
  className,
  offerFreeProduct
}: OfferPromptProps) {
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [showAddFreeItemPrompt, setShowAddFreeItemPrompt] =
    useState<boolean>(false);
  const [quantityNeededToGetOffer, setQuantityNeededToGetOffer] =
    useState<number>(0);
  const [addToBasketQty, setAddToBasketQty] = useState<number>(0);
  const [freeItemBasketQty, setFreeItemBasketQty] = useState<number>(-1);
  const [eligibleFreeItemQty, setEligibleFreeItemQty] = useState<number>(0);
  const halfWayPoint = Math.round(offer.offerQuantityBuy / 2);

  useEffect(() => {
    setShowPrompt(false);
    setShowAddFreeItemPrompt(false);

    if (
      quantityNeededToGetOffer > 0 &&
      ((offer.offerQuantityBuy % 2 !== 0 &&
        quantityNeededToGetOffer < halfWayPoint) ||
        (offer.offerQuantityBuy % 2 == 0 &&
          quantityNeededToGetOffer <= halfWayPoint))
    ) {
      setShowPrompt(true);
    } else if (
      eligibleFreeItemQty > 0 &&
      (offer.offerTypeName === OfferTypes.ExtraFree ||
        (offer.offerTypeName === OfferTypes.FreeProduct &&
          freeItemBasketQty >= 0))
    ) {
      setShowAddFreeItemPrompt(true);
    }
  }, [
    eligibleFreeItemQty,
    quantityNeededToGetOffer,
    halfWayPoint,
    offer.offerQuantityBuy,
    freeItemBasketQty,
    offer.offerTypeName
  ]);

  useEffect(() => {
    if (offer.offerTypeName === OfferTypes.ExtraFree) {
      setAddToBasketQty(quantityNeededToGetOffer + offer.offerQuantityFree);
    } else if (offer.offerTypeName === OfferTypes.FreeProduct) {
      setAddToBasketQty(quantityNeededToGetOffer);
    }
  }, [offer.offerTypeName, offer.offerQuantityFree, quantityNeededToGetOffer]);

  useEffect(() => {
    if (offerFreeProduct?.catalogueRef) {
      getProductQuantityInBasket(offerFreeProduct.catalogueRef).then(
        (result: number) => {
          setFreeItemBasketQty(result);
        }
      );
    }
  }, [offerFreeProduct]);

  useEffect(() => {
    let qtyNeededToGetOffer: number = offer.offerQuantityBuy - basketQty;
    let eligibleFreeItmQty: number = 0;
    let basketQuantity = basketQty;
    if (basketQty > 0) {
      if (offer.offerTypeName === OfferTypes.ExtraFree) {
        const qtyWithOfferItem =
          offer.offerQuantityBuy + offer.offerQuantityFree;
        if (basketQty > offer.offerQuantityBuy) {
          while (basketQuantity >= qtyWithOfferItem) {
            basketQuantity -= qtyWithOfferItem;
          }

          if (basketQuantity === offer.offerQuantityBuy) {
            eligibleFreeItmQty = offer.offerQuantityFree;
          } else if (basketQuantity > offer.offerQuantityBuy) {
            eligibleFreeItmQty = qtyWithOfferItem - basketQuantity;
          }

          qtyNeededToGetOffer = offer.offerQuantityBuy - basketQuantity;
        } else if (basketQty === offer.offerQuantityBuy) {
          eligibleFreeItmQty = offer.offerQuantityFree;
        }
      } else if (offer.offerTypeName === OfferTypes.FreeProduct) {
        if (basketQty > offer.offerQuantityBuy) {
          while (basketQuantity >= offer.offerQuantityBuy) {
            basketQuantity -= offer.offerQuantityBuy;
            eligibleFreeItmQty += offer.offerQuantityFree;
          }

          if (basketQuantity > 0) {
            qtyNeededToGetOffer = offer.offerQuantityBuy - basketQuantity;
          }

          if (!isNaN(freeItemBasketQty) && freeItemBasketQty >= 0)
            eligibleFreeItmQty -= freeItemBasketQty;
        } else if (
          basketQty === offer.offerQuantityBuy &&
          !isNaN(freeItemBasketQty) &&
          freeItemBasketQty < offer.offerQuantityFree
        ) {
          eligibleFreeItmQty = offer.offerQuantityFree - freeItemBasketQty;
        }
      }

      setQuantityNeededToGetOffer(qtyNeededToGetOffer);
      setEligibleFreeItemQty(eligibleFreeItmQty);
    }
  }, [
    basketQty,
    offer.offerQuantityBuy,
    offer.offerQuantityFree,
    offer.offerTypeName,
    eligibleFreeItemQty,
    quantityNeededToGetOffer,
    freeItemBasketQty
  ]);

  return (
    <div className="pt-2 pb-2">
      {showPrompt && (
        <Button
          className={className}
          handleClick={() => {
            if (offer.productCatalogueRef) {
              if (offer.offerTypeName === OfferTypes.ExtraFree) {
                handleQuantityChange(addToBasketQty, offer.productCatalogueRef);
              } else if (offer.offerTypeName === OfferTypes.FreeProduct) {
                if (offer.productCatalogueRef && addToBasketQty > 0) {
                  handleQuantityChange(
                    addToBasketQty,
                    offer.productCatalogueRef
                  );
                  if (offerFreeProduct && offerFreeProduct.catalogueRef) {
                    const newQuantity =
                      offer.offerQuantityFree + eligibleFreeItemQty;
                    handleQuantityChange(
                      newQuantity,
                      offerFreeProduct.catalogueRef
                    );
                    setFreeItemBasketQty(
                      (freeItemBasketQty) => freeItemBasketQty + newQuantity
                    );
                  }
                }
              }

              setShowPrompt(false);
              setShowAddFreeItemPrompt(false);
              setQuantityNeededToGetOffer(offer.offerQuantityBuy);
              setEligibleFreeItemQty(0);
            }
          }}
        >
          Add {quantityNeededToGetOffer} more to get your FREE item
        </Button>
      )}

      {showAddFreeItemPrompt && (
        <Button
          className={className}
          handleClick={() => {
            if (offer.productCatalogueRef) {
              if (offer.offerTypeName === OfferTypes.ExtraFree) {
                handleQuantityChange(
                  eligibleFreeItemQty,
                  offer.productCatalogueRef
                );
              } else if (offer.offerTypeName === OfferTypes.FreeProduct) {
                if (offerFreeProduct && offerFreeProduct.catalogueRef) {
                  handleQuantityChange(
                    eligibleFreeItemQty,
                    offerFreeProduct.catalogueRef
                  );
                  setFreeItemBasketQty(
                    (freeItemBasketQty) =>
                      freeItemBasketQty + eligibleFreeItemQty
                  );
                }
              }
              setShowPrompt(false);
              setShowAddFreeItemPrompt(false);
              setQuantityNeededToGetOffer(offer.offerQuantityBuy);
              setEligibleFreeItemQty(0);
            }
          }}
        >
          Add your FREE item to basket
        </Button>
      )}
    </div>
  );
}

export interface OfferPromptProps {
  offer: OfferDetails;
  handleQuantityChange: (qty: number, catalogueRef: string) => void;
  basketQty: number;
  className: string;
  offerFreeProduct: ProductViewModel | null;
}
